import type { BackendGiftListItemMutation } from "@/lib/mappers/backend-gift-list-schema";
import { toBackendGiftListItemRemoval, toBackendGiftListItemUpsert } from "@/lib/mappers/gift-list-item";
import type { ListedPiece } from "./catalog-types";

/**
 * What a catalog mutation changed, as the batch `PATCH /gift-lists/{id}/items` wants
 * (Requirement 5.4): a diff of the pieces before and after — membership, arrangement and now
 * the featured mark.
 *
 * **Featured used to be deliberately absent from this comparison**, because it had no column
 * (Requirement 5.3a). That absence was the whole defect: the star moved on screen, the diff
 * found nothing to say, the batch came out empty and no request left — so the host marked
 * pieces, came back, and found none marked (reported 2026-10-08). The backend added
 * `GiftListItem.isFeatured` on 2026-10-09 and the mark joined the diff.
 *
 * A piece that did not move is not written, so a bulk "add ten" of which eight were already in
 * writes two. A piece with no "before" at all is an addition from the catalog, and is written.
 * Pure, so the section can compute the batch beside the optimistic result and the two can never
 * disagree. Throws `RangeError` for an arrangement the backend cannot hold (an odd pack), which
 * the section turns into copy before anything is sent.
 */
export function toListItemBatch(
  before: readonly ListedPiece[],
  after: readonly ListedPiece[],
): BackendGiftListItemMutation[] {
  const previous = new Map(before.map((piece) => [piece.id, piece]));
  const batch: BackendGiftListItemMutation[] = [];

  for (const piece of after) {
    const was = previous.get(piece.id);

    // A piece nobody had seen before: it comes from the served catalog, and being on the list
    // now is precisely the change to write. Skipping it is what made "Adicionar" do nothing on
    // the real catalogue (reported 2026-09-24).
    if (was === undefined) {
      if (piece.inList) batch.push(toBackendGiftListItemUpsert(piece));
      continue;
    }

    if (was.inList && !piece.inList) {
      batch.push(toBackendGiftListItemRemoval(piece));
    } else if (
      piece.inList &&
      (!was.inList ||
        was.total !== piece.total ||
        was.pack !== piece.pack ||
        // Featured is a column since 2026-10-09. While it was not, this comparison deliberately
        // left it out — and that is what made the star move on screen and nothing be written.
        was.featured !== piece.featured)
    ) {
      batch.push(toBackendGiftListItemUpsert(piece));
    }
  }

  return batch;
}

/**
 * What the list is known to hold after a batch that landed **in part**: the pieces whose write
 * went through take the screen's version, every other piece stays as it was. A landed piece the
 * list had never held (an addition from the served catalog) joins it.
 */
export function landBatch(
  saved: readonly ListedPiece[],
  target: readonly ListedPiece[],
  landed: ReadonlySet<string>,
): ListedPiece[] {
  const wanted = new Map(target.map((piece) => [piece.id, piece]));
  const known = new Set(saved.map((piece) => piece.id));

  const next = saved.map((piece) => (landed.has(piece.id) ? (wanted.get(piece.id) ?? piece) : piece));
  for (const id of landed) {
    const piece = wanted.get(id);
    if (!known.has(id) && piece !== undefined) next.push(piece);
  }

  return next;
}

/**
 * The screen with the refused pieces put back to what the list holds, and **only** them: a press
 * made while the write was in flight, and every piece that landed, stay as they are. A refused
 * piece the list never held leaves the screen's pieces, as it did when the whole batch failed.
 */
export function revertFailed(
  current: readonly ListedPiece[],
  saved: readonly ListedPiece[],
  failed: ReadonlySet<string>,
): ListedPiece[] {
  const held = new Map(saved.map((piece) => [piece.id, piece]));

  return current.flatMap((piece) => {
    if (!failed.has(piece.id)) return [piece];
    const was = held.get(piece.id);

    return was === undefined ? [] : [was];
  });
}
